import { ApolloClient, createHttpLink, InMemoryCache } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import cookies from 'js-cookie';

import config from '../../app-config';
import { AUTH_TOKEN, USER_ID } from '../constants-togather/auth';
import {
  getImpersonatingUserUuid,
  getLatestCognitoToken,
  isCognitoAuthentication,
  isImpersonationSession,
} from '../helpers/auth/cognito';

const httpLink = createHttpLink({
  uri: config.graphqlApi,
});

const authLink = setContext(async (_, { headers, authToken, useNewAuth }) => {
  // get the authentication token from cookies if it exists
  const token = authToken || cookies.get(AUTH_TOKEN);
  const shouldUseNewAuth =
    Boolean(useNewAuth) || (token && isCognitoAuthentication(token));

  if (token && shouldUseNewAuth) {
    const bearerToken =
      useNewAuth && authToken
        ? authToken
        : (await getLatestCognitoToken()) || authToken;

    return {
      headers: {
        ...headers,
        authorization: `Bearer ${bearerToken}`,
        'x-new-auth': true,
        // The user id is derived server-side from the verified id token, so it
        // is not sent for normal requests. During impersonation we send the
        // admin-selected target (honoured by the backend only for admins) —
        // without it the backend has no id, and customer resolvers that filter
        // by `user_id: ctx.user.id` would query with `undefined` (Prisma drops
        // the filter → loads the whole table → OOM).
        ...(isImpersonationSession() && {
          'x-user-id': cookies.get(USER_ID),
          'x-impersonating-user-uuid': getImpersonatingUserUuid(),
        }),
      },
    };
  }

  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : '',
    },
  };
});

export const client = new ApolloClient({
  link: authLink.concat(httpLink),
  cache: new InMemoryCache(),
});
